import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Plus, Truck, Edit, Trash2, Fuel, Clock, AlertTriangle, Settings, FileText, Upload, Download, File, Search, Filter, Archive } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";
import { READING_TYPES, MACHINE_STATUSES, usesKm, usesHours } from "../components/equipment/readingType";
import { getUser, getToken } from "../utils/authService";
import BackNavigation from "../components/BackNavigation";
import { FuelAnalyticsTab, DailyLogsTab, FuelTrackingTab, EquipmentMasterTab, BulkLogEntryTab, MismatchReportTab, BulkFuelEntryTab, UtilizationReportTab, ReadingsWizardTab } from "../components/equipment";
import { exportToXlsx } from "../components/materials/exportXlsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EquipmentManagement = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [fuelSummary, setFuelSummary] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [fuelIssues, setFuelIssues] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [activeTab, setActiveTab] = useState("master");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("equipment");
  const [editingEquipment, setEditingEquipment] = useState(null);
  
  // Archive dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [equipmentToArchive, setEquipmentToArchive] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveRemarks, setArchiveRemarks] = useState("");
  
  // Daily Log filters and bulk selection
  const [dailyLogSearch, setDailyLogSearch] = useState("");
  const [dailyLogDateFilter, setDailyLogDateFilter] = useState({ start: "", end: "" });
  const [dailyLogEquipmentFilter, setDailyLogEquipmentFilter] = useState("");
  const [selectedDailyLogIds, setSelectedDailyLogIds] = useState([]);
  const [equipmentSearch, setEquipmentSearch] = useState("");

  const [equipmentForm, setEquipmentForm] = useState({
    name: "", equipment_id: "", equipment_type: "Heavy", make: "", model: "",
    year_of_manufacture: "", capacity: "", fuel_type: "Diesel", tank_capacity: "",
    avg_consumption: "", owner_type: "Own", hire_rate: "", status: "Available", reading_type: "both",
    current_operator: "", last_service_date: "", next_service_due: ""
  });

  const [dailyLogForm, setDailyLogForm] = useState({
    equipment_id: "", date: new Date().toISOString().split('T')[0],
    // KM readings
    km_start_reading: "", km_end_reading: "",
    // Hour readings
    hour_start_reading: "", hour_end_reading: "",
    // Auto-calculated differences
    km_used: "", hours_used: "",
    fuel_issued: "",
    operator_name: "", activity: "", location: "", breakdown_hours: "",
    breakdown_reason: "", remarks: "", performance_metric: "", performance_unit: "KM/hr", machine_status: "Working"
  });
  
  // State for editing daily log
  const [editingDailyLog, setEditingDailyLog] = useState(null);

  const [fuelForm, setFuelForm] = useState({
    equipment_id: "", material_id: "", date: new Date().toISOString().split('T')[0],
    quantity: "", 
    opening_km_reading: 0, opening_hour_reading: 0,
    current_hour_reading: "", current_km_reading: "",
    issued_by: "", received_by: "", remarks: "", daily_log_id: ""
  });
  const [lastFuelReadings, setLastFuelReadings] = useState(null);
  const [lastLogReadings, setLastLogReadings] = useState(null);
  const [logsForLinking, setLogsForLinking] = useState([]);
  const [editingFuelIssue, setEditingFuelIssue] = useState(null);
  const [logHistoryDialogOpen, setLogHistoryDialogOpen] = useState(false);
  const [logHistoryTab, setLogHistoryTab] = useState("table");
  const [logHistoryEquipment, setLogHistoryEquipment] = useState(null);
  const [logHistory, setLogHistory] = useState([]);
  const [fuelForLogDate, setFuelForLogDate] = useState(null);

  // Document State
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedEquipmentForDocs, setSelectedEquipmentForDocs] = useState(null);
  const [equipmentDocuments, setEquipmentDocuments] = useState([]);
  const [equipmentDocTypes, setEquipmentDocTypes] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({
    document_type: "",
    document_name: "",
    file: null
  });

  const currentUser = getUser() || {};
  const isMasterAdmin = currentUser.role === "Master Admin";

  useEffect(() => {
    fetchProject();
    fetchEquipment();
    fetchFuelSummary();
    fetchDailyLogs();
    fetchFuelIssues();
    fetchMaterials();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Failed to fetch project");
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await apiClient.get(`/equipment/${projectId}`);
      setEquipment(response.data);
    } catch (error) {
      toast.error("Failed to fetch equipment");
    }
  };

  const fetchFuelSummary = async () => {
    try {
      const response = await apiClient.get(`/equipment/fuel-summary/${projectId}`);
      setFuelSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch fuel summary");
    }
  };

  const fetchDailyLogs = async () => {
    try {
      const response = await apiClient.get(`/equipment/daily-log/${projectId}`);
      setDailyLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch daily logs");
    }
  };

  const fetchFuelIssues = async () => {
    try {
      const response = await apiClient.get(`/equipment/fuel-issues/${projectId}`);
      setFuelIssues(response.data);
    } catch (error) {
      console.error("Failed to fetch fuel issues");
    }
  };

  // Fetch last fuel readings when equipment is selected for fuel issue
  const fetchLastFuelReadings = async (equipmentId) => {
    if (!equipmentId) {
      setLastFuelReadings(null);
      return;
    }
    try {
      const response = await apiClient.get(`/equipment/last-readings/${equipmentId}`);
      setLastFuelReadings(response.data);
      setFuelForm(prev => ({
        ...prev,
        opening_km_reading: response.data.closing_km_reading || 0,
        opening_hour_reading: response.data.closing_hour_reading || 0
      }));
    } catch (error) {
      console.error("Failed to fetch last fuel readings");
    }
  };

  const fetchLastLogReadings = async (equipmentId) => {
    if (!equipmentId) {
      setLastLogReadings(null);
      return;
    }
    try {
      const response = await apiClient.get(`/equipment/last-log-readings/${equipmentId}`);
      setLastLogReadings(response.data);
      if (response.data.has_previous) {
        setDailyLogForm(prev => ({
          ...prev,
          km_start_reading: response.data.closing_km || "",
          hour_start_reading: response.data.closing_hour || ""
        }));
      }
    } catch (error) {
      console.error("Failed to fetch last log readings");
    }
  };

  const fetchLogsForLinking = async (equipmentId, targetDate) => {
    if (!equipmentId || !targetDate) { setLogsForLinking([]); return; }
    try {
      const response = await apiClient.get(`/equipment/daily-logs-for-linking/${equipmentId}?target_date=${targetDate}`);
      setLogsForLinking(response.data);
    } catch (error) {
      console.error("Failed to fetch logs for linking");
      setLogsForLinking([]);
    }
  };

  const fetchLogHistory = async (eq) => {
    setLogHistoryEquipment(eq);
    setLogHistoryDialogOpen(true);
    setLogHistoryTab("table");
    try {
      const response = await apiClient.get(`/equipment/logs-history/${eq.id}?limit=100`);
      setLogHistory(response.data);
    } catch (error) {
      toast.error("Failed to fetch log history");
      setLogHistory([]);
    }
  };

  const fetchFuelForLogDate = async (equipmentId, date) => {
    if (!equipmentId || !date) { setFuelForLogDate(null); return; }
    try {
      const fuel = fuelIssues.find(f => f.equipment_id === equipmentId && f.date === date);
      setFuelForLogDate(fuel || null);
    } catch (error) {
      setFuelForLogDate(null);
    }
  };

  const copyFuelReadingsToLog = () => {
    if (!fuelForLogDate) return;
    setDailyLogForm(prev => ({
      ...prev,
      km_end_reading: fuelForLogDate.closing_km_reading?.toString() || fuelForLogDate.current_km_reading?.toString() || prev.km_end_reading,
      hour_end_reading: fuelForLogDate.closing_hour_reading?.toString() || fuelForLogDate.current_hour_reading?.toString() || prev.hour_end_reading
    }));
    toast.success("Fuel readings copied to form");
  };

  const exportLogHistory = async () => {
    if (logHistory.length === 0) return toast.error("No logs to export");
    const eq = logHistoryEquipment;
    await exportToXlsx({
      fileName: `Daily_Logs_${eq?.name || "Equipment"}_${new Date().toISOString().split("T")[0]}.xlsx`,
      title: `Daily Log History - ${eq?.name || "Equipment"}`,
      subtitle: `Equipment ID: ${eq?.equipment_id || "N/A"}  |  Total Logs: ${logHistory.length}`,
      sheetName: "Daily Logs",
      columns: [
        { header: "Date", key: "date", width: 12 },
        { header: "KM Open", key: "km_start_reading", width: 12, numFmt: "#,##0.0" },
        { header: "KM Close", key: "km_end_reading", width: 12, numFmt: "#,##0.0" },
        { header: "KM Run", key: "km_run", width: 10, numFmt: "#,##0.0", total: true },
        { header: "Hr Open", key: "hour_start_reading", width: 12, numFmt: "#,##0.0" },
        { header: "Hr Close", key: "hour_end_reading", width: 12, numFmt: "#,##0.0" },
        { header: "Hours Used", key: "hours_used", width: 10, numFmt: "#,##0.0", total: true },
        { header: "Operator", key: "operator_name", width: 18 },
        { header: "Activity", key: "activity", width: 28 },
        { header: "Fuel Linked (L)", key: "fuel_linked", width: 14, numFmt: "#,##0.0", total: true },
        { header: "Fuel Issues", key: "fuel_count", width: 12 },
        { header: "Remarks", key: "remarks", width: 24 }
      ],
      rows: logHistory.map(l => ({
        ...l,
        km_run: Math.max(0, (l.km_end_reading || 0) - (l.km_start_reading || 0)),
        fuel_linked: l.linked_fuel?.reduce((s, f) => s + f.quantity, 0) || 0,
        fuel_count: l.linked_fuel?.length || 0
      }))
    });
    toast.success("Log history exported to Excel");
  };


  const fetchMaterials = async () => {
    try {
      const response = await apiClient.get(`/materials/${projectId}`);
      // Filter fuel materials (diesel, petrol)
      const fuelMaterials = response.data.filter(m => 
        m.name.toLowerCase().includes('diesel') || 
        m.name.toLowerCase().includes('petrol') ||
        m.name.toLowerCase().includes('fuel')
      );
      setMaterials(fuelMaterials.length > 0 ? fuelMaterials : response.data);
    } catch (error) {
      console.error("Failed to fetch materials");
    }
  };

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...equipmentForm,
        year_of_manufacture: equipmentForm.year_of_manufacture ? parseInt(equipmentForm.year_of_manufacture) : null,
        tank_capacity: parseFloat(equipmentForm.tank_capacity || 0),
        avg_consumption: parseFloat(equipmentForm.avg_consumption || 0),
        hire_rate: parseFloat(equipmentForm.hire_rate || 0)
      };

      if (editingEquipment) {
        await apiClient.put(`/equipment/${editingEquipment.id}`, data);
        toast.success("Equipment updated");
      } else {
        await apiClient.post('/equipment', { ...data, project_id: projectId });
        toast.success("Equipment added");
      }
      closeDialog();
      fetchEquipment();
      fetchFuelSummary();
    } catch (error) {
      toast.error(`Failed to ${editingEquipment ? 'update' : 'add'} equipment`);
    }
  };

  const handleDailyLogSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate KM Run from readings (ONLY from KM meter)
      const kmUsed = dailyLogForm.km_end_reading && dailyLogForm.km_start_reading 
        ? Math.max(0, parseFloat(dailyLogForm.km_end_reading) - parseFloat(dailyLogForm.km_start_reading))
        : 0;
      // Calculate Hours Used from Hour meter OR manual input (NEVER from KM)
      const hoursFromMeter = dailyLogForm.hour_end_reading && dailyLogForm.hour_start_reading && parseFloat(dailyLogForm.hour_end_reading) > parseFloat(dailyLogForm.hour_start_reading)
        ? parseFloat(dailyLogForm.hour_end_reading) - parseFloat(dailyLogForm.hour_start_reading)
        : null;
      const hoursUsed = hoursFromMeter !== null ? hoursFromMeter : parseFloat(dailyLogForm.hours_used || 0);

      const data = {
        equipment_id: dailyLogForm.equipment_id,
        date: dailyLogForm.date,
        project_id: projectId,
        // KM readings
        km_start_reading: parseFloat(dailyLogForm.km_start_reading || 0),
        km_end_reading: parseFloat(dailyLogForm.km_end_reading || 0),
        km_used: kmUsed,
        // Hour readings
        hour_start_reading: parseFloat(dailyLogForm.hour_start_reading || 0),
        hour_end_reading: parseFloat(dailyLogForm.hour_end_reading || 0),
        hours_used: hoursUsed,
        // Legacy fields for backward compatibility
        start_reading: parseFloat(dailyLogForm.hour_start_reading || dailyLogForm.km_start_reading || 0),
        end_reading: parseFloat(dailyLogForm.hour_end_reading || dailyLogForm.km_end_reading || 0),
        fuel_issued: parseFloat(dailyLogForm.fuel_issued || 0),
        breakdown_hours: parseFloat(dailyLogForm.breakdown_hours || 0),
        performance_metric: parseFloat(dailyLogForm.performance_metric || 0),
        performance_unit: dailyLogForm.performance_unit,
        operator_name: dailyLogForm.operator_name,
        activity: dailyLogForm.activity,
        location: dailyLogForm.location,
        breakdown_reason: dailyLogForm.breakdown_reason,
        remarks: dailyLogForm.remarks,
        machine_status: dailyLogForm.machine_status || "Working"
      };
      
      if (editingDailyLog) {
        await apiClient.put(`/equipment/daily-log/${editingDailyLog.id}`, data);
        toast.success("Daily log updated");
      } else {
        await apiClient.post('/equipment/daily-log', data);
        toast.success("Daily log recorded");
      }
      closeDialog();
      setEditingDailyLog(null);
      fetchDailyLogs();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record daily log");
    }
  };
  
  const handleEditDailyLog = (log) => {
    setEditingDailyLog(log);
    setDailyLogForm({
      equipment_id: log.equipment_id,
      date: log.date,
      km_start_reading: log.km_start_reading?.toString() || "",
      km_end_reading: log.km_end_reading?.toString() || "",
      hour_start_reading: log.hour_start_reading?.toString() || log.start_reading?.toString() || "",
      hour_end_reading: log.hour_end_reading?.toString() || log.end_reading?.toString() || "",
      km_used: log.km_used?.toString() || "",
      hours_used: log.hours_used?.toString() || "",
      fuel_issued: log.fuel_issued?.toString() || "",
      operator_name: log.operator_name || "",
      activity: log.activity || "",
      location: log.location || "",
      breakdown_hours: log.breakdown_hours?.toString() || "",
      breakdown_reason: log.breakdown_reason || "",
      remarks: log.remarks || "",
      performance_metric: log.performance_metric?.toString() || "",
      performance_unit: log.performance_unit || "KM/hr", machine_status: log.machine_status || "Working"
    });
    setDialogType("daily-log");
    setDialogOpen(true);
  };
  
  const handleDeleteDailyLog = async (logId) => {
    if (!window.confirm("Delete this daily log entry?")) return;
    try {
      await apiClient.delete(`/equipment/daily-log/${logId}`);
      toast.success("Daily log deleted");
      fetchDailyLogs();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete daily log");
    }
  };

  const handleFuelSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...fuelForm,
        project_id: projectId,
        quantity: parseFloat(fuelForm.quantity),
        current_hour_reading: parseFloat(fuelForm.current_hour_reading || 0),
        current_km_reading: parseFloat(fuelForm.current_km_reading || 0),
        daily_log_id: fuelForm.daily_log_id || ""
      };
      if (editingFuelIssue) {
        await apiClient.put(`/equipment/fuel-issue/${editingFuelIssue.id}`, {
          date: data.date, quantity: data.quantity, closing_km_reading: data.current_km_reading,
          closing_hour_reading: data.current_hour_reading, issued_by: data.issued_by,
          received_by: data.received_by, remarks: data.remarks, daily_log_id: data.daily_log_id
        });
        toast.success("Fuel issue updated");
      } else {
        const response = await apiClient.post('/equipment/fuel-issue', data);
        toast.success(`Fuel issued - ${response.data.issue_number}`);
      }
      closeDialog();
      fetchFuelIssues();
      fetchFuelSummary();
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to issue fuel");
    }
  };

  const handleEditFuelIssue = (fuel) => {
    setEditingFuelIssue(fuel);
    setFuelForm({
      equipment_id: fuel.equipment_id, material_id: fuel.material_id,
      date: fuel.date, quantity: fuel.quantity?.toString() || "",
      opening_km_reading: fuel.opening_km_reading || 0, opening_hour_reading: fuel.opening_hour_reading || 0,
      current_km_reading: fuel.closing_km_reading?.toString() || "", current_hour_reading: fuel.closing_hour_reading?.toString() || "",
      issued_by: fuel.issued_by || "", received_by: fuel.received_by || "",
      remarks: fuel.remarks || "", daily_log_id: fuel.daily_log_id || ""
    });
    setLastFuelReadings({ has_previous: true, closing_km_reading: fuel.opening_km_reading, closing_hour_reading: fuel.opening_hour_reading });
    fetchLogsForLinking(fuel.equipment_id, fuel.date);
    setDialogType("fuel");
    setDialogOpen(true);
  };

  const handleEdit = (eq) => {
    setEditingEquipment(eq);
    setEquipmentForm({
      name: eq.name, equipment_id: eq.equipment_id, equipment_type: eq.equipment_type || "Heavy",
      make: eq.make || "", model: eq.model || "", year_of_manufacture: eq.year_of_manufacture?.toString() || "",
      capacity: eq.capacity || "", fuel_type: eq.fuel_type || "Diesel",
      tank_capacity: eq.tank_capacity?.toString() || "", avg_consumption: eq.avg_consumption?.toString() || "",
      owner_type: eq.owner_type || "Own", hire_rate: eq.hire_rate?.toString() || "",
      status: eq.status || "Available", reading_type: eq.reading_type || "both", current_operator: eq.current_operator || "",
      last_service_date: eq.last_service_date || "", next_service_due: eq.next_service_due || ""
    });
    setDialogType("equipment");
    setDialogOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete equipment "${name}"?`)) return;
    try {
      await apiClient.delete(`/equipment/${id}`);
      toast.success("Equipment deleted");
      fetchEquipment();
    } catch (error) {
      toast.error("Failed to delete equipment");
    }
  };
  
  // Archive equipment (de-hire)
  const openArchiveDialog = (eq) => {
    setEquipmentToArchive(eq);
    setArchiveReason("");
    setArchiveRemarks("");
    setArchiveDialogOpen(true);
  };
  
  const handleArchiveEquipment = async () => {
    if (!equipmentToArchive || !archiveReason.trim()) {
      toast.error("Please provide a reason for archiving");
      return;
    }
    
    try {
      await apiClient.post(`/equipment/${equipmentToArchive.id}/archive`, {
        reason: archiveReason,
        remarks: archiveRemarks
      });
      toast.success("Equipment archived (de-hired) successfully");
      setArchiveDialogOpen(false);
      setEquipmentToArchive(null);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive equipment");
    }
  };
  
  // Bulk delete daily logs
  const handleBulkDeleteDailyLogs = async () => {
    if (selectedDailyLogIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedDailyLogIds.length} daily log entries?`)) return;
    
    try {
      let deleted = 0;
      for (const logId of selectedDailyLogIds) {
        await apiClient.delete(`/equipment/daily-log/${logId}`);
        deleted++;
      }
      toast.success(`Deleted ${deleted} daily log entries`);
      setSelectedDailyLogIds([]);
      fetchDailyLogs();
      fetchEquipment();
    } catch (error) {
      toast.error("Failed to delete some entries");
    }
  };
  
  // Filter daily logs
  const filteredDailyLogs = dailyLogs.filter(log => {
    // Equipment filter
    if (dailyLogEquipmentFilter && log.equipment_id !== dailyLogEquipmentFilter) return false;
    // Date range filter
    if (dailyLogDateFilter.start && log.date < dailyLogDateFilter.start) return false;
    if (dailyLogDateFilter.end && log.date > dailyLogDateFilter.end) return false;
    // Search filter (equipment name or operator)
    if (dailyLogSearch) {
      const search = dailyLogSearch.toLowerCase();
      if (!log.equipment_name?.toLowerCase().includes(search) && 
          !log.operator_name?.toLowerCase().includes(search) &&
          !log.activity?.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  const openDialog = (type) => {
    setDialogType(type);
    setEditingEquipment(null);
    setEditingDailyLog(null);
    if (type === "equipment") {
      setEquipmentForm({
        name: "", equipment_id: "", equipment_type: "Heavy", make: "", model: "",
        year_of_manufacture: "", capacity: "", fuel_type: "Diesel", tank_capacity: "",
        avg_consumption: "", owner_type: "Own", hire_rate: "", status: "Available", reading_type: "both",
        current_operator: "", last_service_date: "", next_service_due: ""
      });
    } else if (type === "daily-log") {
      const today = new Date().toISOString().split('T')[0];
      setDailyLogForm({
        equipment_id: equipment[0]?.id || "", date: today,
        km_start_reading: "", km_end_reading: "",
        hour_start_reading: "", hour_end_reading: "",
        km_used: "", hours_used: "", fuel_issued: "",
        operator_name: "", activity: "", location: "", breakdown_hours: "",
        breakdown_reason: "", remarks: "", performance_metric: "", performance_unit: "KM/hr", machine_status: "Working"
      });
      setLastLogReadings(null);
      setFuelForLogDate(null);
      if (equipment[0]?.id) {
        fetchLastLogReadings(equipment[0].id);
        fetchFuelForLogDate(equipment[0].id, today);
      }
    } else if (type === "fuel") {
      setFuelForm({
        equipment_id: equipment[0]?.id || "", material_id: materials[0]?.id || "",
        date: new Date().toISOString().split('T')[0], quantity: "",
        opening_km_reading: 0, opening_hour_reading: 0,
        current_hour_reading: "", current_km_reading: "", issued_by: currentUser.name || "", received_by: "", remarks: "", daily_log_id: ""
      });
      setLastFuelReadings(null);
      setEditingFuelIssue(null);
      setLogsForLinking([]);
      if (equipment[0]?.id) {
        fetchLastFuelReadings(equipment[0].id);
        fetchLogsForLinking(equipment[0].id, new Date().toISOString().split('T')[0]);
      }
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingEquipment(null);
    setEditingDailyLog(null);
    setEditingFuelIssue(null);
    setLogsForLinking([]);
  };

  // Document Functions
  const openDocumentDialog = async (eq) => {
    setSelectedEquipmentForDocs(eq);
    setDocumentDialogOpen(true);
    await fetchEquipmentDocuments(eq.id);
    await fetchEquipmentDocTypes();
  };

  const fetchEquipmentDocuments = async (equipmentId) => {
    try {
      const response = await apiClient.get(`/equipment/${equipmentId}/documents`);
      setEquipmentDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents");
    }
  };

  const fetchEquipmentDocTypes = async () => {
    try {
      const response = await apiClient.get('/document-types/equipment');
      setEquipmentDocTypes(response.data);
    } catch (error) {
      setEquipmentDocTypes([
        "Registration Certificate", "Insurance", "Service Record", "Purchase Invoice",
        "Warranty Card", "Fitness Certificate", "Permit", "PUC Certificate",
        "Tax Receipt", "Lease Agreement", "Other"
      ]);
    }
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!docForm.file || !docForm.document_type) {
      toast.error("Please select a file and document type");
      return;
    }

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', docForm.file);
      formData.append('document_type', docForm.document_type);
      formData.append('document_name', docForm.document_name || docForm.file.name);

      await apiClient.post(`/equipment/${selectedEquipmentForDocs.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Document uploaded successfully");
      setDocForm({ document_type: "", document_name: "", file: null });
      await fetchEquipmentDocuments(selectedEquipmentForDocs.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const token = getToken();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/equipment/${selectedEquipmentForDocs.id}/documents/${doc.id}/download`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = doc.document_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Document downloaded");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!window.confirm(`Delete document "${doc.document_name}"?`)) return;
    
    try {
      await apiClient.delete(`/equipment/${selectedEquipmentForDocs.id}/documents/${doc.id}`);
      toast.success("Document deleted");
      await fetchEquipmentDocuments(selectedEquipmentForDocs.id);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const totalEquipment = equipment.length;
  const totalHours = equipment.reduce((sum, e) => sum + (e.total_hours || 0), 0);
  const logEq = equipment.find(e => e.id === dailyLogForm.equipment_id);
  const fuelEq = equipment.find(e => e.id === fuelForm.equipment_id);
  const logEqKm = usesKm(logEq), logEqHr = usesHours(logEq);
  const fuelEqKm = usesKm(fuelEq), fuelEqHr = usesHours(fuelEq);
  const totalFuel = fuelSummary.reduce((sum, f) => sum + (f.total_fuel_consumed || 0), 0);

  return (
    <div>
      <BackNavigation title="Equipment Management" projectName={project?.name} />
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-primary tracking-tight">EQUIPMENT MANAGEMENT</h1>
          <p className="text-slate-600 mt-2 font-body">Equipment database, daily logs & fuel tracking</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => openDialog("daily-log")} data-testid="add-daily-log-btn" className="btn-outline flex items-center space-x-2 border-accent text-accent hover:bg-accent/10">
            <Clock className="w-4 h-4" />
            <span>Daily Log</span>
          </button>
          <button onClick={() => openDialog("fuel")} data-testid="add-fuel-btn" className="btn-outline flex items-center space-x-2 border-warning text-warning hover:bg-warning/10">
            <Fuel className="w-4 h-4" />
            <span>Issue Fuel</span>
          </button>
          <button onClick={() => openDialog("equipment")} data-testid="add-equipment-btn" className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Equipment</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Equipment</p>
          <p className="font-heading font-black text-3xl text-primary font-mono">{totalEquipment}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Hours Run</p>
          <p className="font-heading font-black text-3xl text-accent font-mono">{totalHours.toFixed(0)}</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Total Fuel Consumed</p>
          <p className="font-heading font-black text-3xl text-warning font-mono">{totalFuel.toFixed(0)} L</p>
        </div>
        <div className="stat-card">
          <p className="text-xs uppercase tracking-widest text-slate-600 font-medium mb-2">Fuel Issues Today</p>
          <p className="font-heading font-black text-3xl text-success font-mono">
            {fuelIssues.filter(f => f.date === new Date().toISOString().split('T')[0]).length}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-slate-100 p-1 rounded-sm w-full max-w-full sm:w-fit overflow-x-auto">
        {[
          { key: "master", label: "Equipment Master" },
          { key: "daily-logs", label: "Daily Logs" },
          { key: "bulk-entry", label: "Bulk Log Entry" },
          { key: "fuel-tracking", label: "Fuel Tracking" },
          { key: "bulk-fuel", label: "Bulk Fuel Entry" },
          { key: "fuel-analytics", label: "Fuel Analytics" },
          { key: "utilization", label: "Utilization Report" },
          { key: "mismatch-report", label: "Mismatch Report" },
          { key: "readings-wizard", label: "Fix Readings" }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors whitespace-nowrap ${
              activeTab === tab.key ? 'bg-white text-primary shadow-sm' : 'text-slate-600 hover:text-primary'
            }`}
            data-testid={`tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Equipment Master Tab */}
      {activeTab === "master" && (
        <EquipmentMasterTab
          equipment={equipment}
          isMasterAdmin={isMasterAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDocuments={openDocumentDialog}
          onArchive={openArchiveDialog}
          onViewLogHistory={fetchLogHistory}
        />
      )}

      {/* Daily Logs Tab */}
      {activeTab === "daily-logs" && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="stat-card">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by equipment, operator, activity..."
                  value={dailyLogSearch}
                  onChange={(e) => setDailyLogSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                  data-testid="daily-log-search"
                />
              </div>
              
              {/* Equipment Filter */}
              <select
                value={dailyLogEquipmentFilter}
                onChange={(e) => setDailyLogEquipmentFilter(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
              >
                <option value="">All Equipment</option>
                {equipment.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
              
              {/* Date Range */}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dailyLogDateFilter.start}
                  onChange={(e) => setDailyLogDateFilter({...dailyLogDateFilter, start: e.target.value})}
                  className="px-3 py-2 border border-slate-300 rounded-sm focus:outline-none text-sm font-mono"
                  placeholder="From"
                />
                <span className="text-slate-400">to</span>
                <input
                  type="date"
                  value={dailyLogDateFilter.end}
                  onChange={(e) => setDailyLogDateFilter({...dailyLogDateFilter, end: e.target.value})}
                  className="px-3 py-2 border border-slate-300 rounded-sm focus:outline-none text-sm font-mono"
                  placeholder="To"
                />
              </div>
              
              {/* Clear Filters */}
              {(dailyLogSearch || dailyLogEquipmentFilter || dailyLogDateFilter.start || dailyLogDateFilter.end) && (
                <button
                  onClick={() => {
                    setDailyLogSearch("");
                    setDailyLogEquipmentFilter("");
                    setDailyLogDateFilter({ start: "", end: "" });
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  Clear Filters
                </button>
              )}
            </div>
            
            {/* Bulk Actions */}
            {isMasterAdmin && selectedDailyLogIds.length > 0 && (
              <div className="mt-4 flex items-center gap-4 p-3 bg-red-50 border border-red-200 rounded-sm">
                <span className="text-sm font-medium text-red-700">
                  {selectedDailyLogIds.length} entries selected
                </span>
                <button
                  onClick={handleBulkDeleteDailyLogs}
                  className="px-3 py-1 bg-error text-white text-sm rounded-sm hover:bg-error/90"
                >
                  Delete Selected
                </button>
                <button
                  onClick={() => setSelectedDailyLogIds([])}
                  className="text-sm text-slate-600 hover:underline"
                >
                  Clear Selection
                </button>
              </div>
            )}
          </div>
          
          {/* Table */}
          <div className="stat-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  {isMasterAdmin && (
                    <th className="text-center py-3 px-2 w-10">
                      <input
                        type="checkbox"
                        checked={selectedDailyLogIds.length === filteredDailyLogs.length && filteredDailyLogs.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedDailyLogIds(filteredDailyLogs.map(l => l.id));
                          } else {
                            setSelectedDailyLogIds([]);
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                    </th>
                  )}
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Equipment</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">KM</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Hours</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Log Fuel</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Diesel Issued</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Operator</th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Activity</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Breakdown</th>
                  {isMasterAdmin && (
                    <th className="text-center py-3 px-4 text-xs uppercase tracking-widest text-slate-700 font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredDailyLogs.map((log) => (
                  <tr key={log.id} className={`border-b border-slate-200 hover:bg-slate-50 ${log.breakdown_hours > 0 ? 'bg-red-50' : ''} ${selectedDailyLogIds.includes(log.id) ? 'bg-primary/5' : ''}`}>
                    {isMasterAdmin && (
                      <td className="py-3 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedDailyLogIds.includes(log.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedDailyLogIds([...selectedDailyLogIds, log.id]);
                            } else {
                              setSelectedDailyLogIds(selectedDailyLogIds.filter(id => id !== log.id));
                            }
                          }}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                      </td>
                    )}
                    <td className="py-3 px-4 font-mono text-sm">{log.date}</td>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{log.equipment_name}</p>
                        {log.equipment_reg && (
                          <p className="text-xs font-mono text-slate-500">{log.equipment_reg}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-primary">
                      {log.km_used > 0 ? `${log.km_used?.toFixed(1)} km` : '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm font-semibold text-accent">{log.hours_used?.toFixed(1)} hrs</td>
                    <td className="py-3 px-4 text-right font-mono text-sm text-slate-500">{log.fuel_issued?.toFixed(0) || 0} L</td>
                    <td className="py-3 px-4 text-right">
                      {(() => {
                        // Find fuel issues for this equipment on this date
                        const fuelIssuedOnDate = fuelIssues.filter(f => 
                          f.equipment_id === log.equipment_id && f.date === log.date
                        );
                        const totalFuel = fuelIssuedOnDate.reduce((sum, f) => sum + (f.quantity || 0), 0);
                        if (totalFuel > 0) {
                          return (
                            <div className="text-warning font-mono text-sm font-bold" title={`${fuelIssuedOnDate.length} issue(s)`}>
                              ⛽ {totalFuel.toFixed(0)} L
                            </div>
                          );
                        }
                        return <span className="text-slate-400 text-sm">-</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4 text-sm">{log.operator_name}</td>
                    <td className="py-3 px-4 text-sm">{log.activity}</td>
                    <td className="py-3 px-4 text-right font-mono text-sm">
                      {log.breakdown_hours > 0 && (
                        <span className="text-error font-semibold">{log.breakdown_hours} hrs</span>
                      )}
                    </td>
                    {isMasterAdmin && (
                      <td className="py-3 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button 
                            onClick={() => handleEditDailyLog(log)} 
                            className="p-1 hover:bg-slate-200 rounded" 
                            title="Edit"
                            data-testid={`edit-daily-log-${log.id}`}
                          >
                            <Edit className="w-4 h-4 text-primary" />
                          </button>
                          <button 
                            onClick={() => handleDeleteDailyLog(log.id)} 
                            className="p-1 hover:bg-red-100 rounded" 
                            title="Delete"
                            data-testid={`delete-daily-log-${log.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-error" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredDailyLogs.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                {dailyLogs.length === 0 ? "No daily logs recorded yet." : "No logs match your filters."}
              </div>
            )}
            
            {/* Results count */}
            {dailyLogs.length > 0 && (
              <div className="mt-4 text-sm text-slate-500">
                Showing {filteredDailyLogs.length} of {dailyLogs.length} entries
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fuel Tracking Tab */}
      {activeTab === "fuel-tracking" && (
        <FuelTrackingTab
          fuelSummary={fuelSummary}
          fuelIssues={fuelIssues}
          onViewAnalytics={() => setActiveTab("fuel-analytics")}
          onEditFuelIssue={handleEditFuelIssue}
          isMasterAdmin={isMasterAdmin}
        />
      )}

      {/* Fuel Analytics Tab */}
      {activeTab === "fuel-analytics" && (
        <FuelAnalyticsTab projectId={projectId} isMasterAdmin={isMasterAdmin} />
      )}

      {/* Bulk Entry Tab */}
      {activeTab === "bulk-entry" && (
        <BulkLogEntryTab projectId={projectId} equipment={equipment} onRefresh={() => { fetchDailyLogs(); }} />
      )}

      {/* Mismatch Report Tab */}
      {activeTab === "mismatch-report" && (
        <MismatchReportTab projectId={projectId} equipment={equipment} isMasterAdmin={isMasterAdmin} />
      )}

      {/* Bulk Fuel Entry Tab */}
      {activeTab === "bulk-fuel" && (
        <BulkFuelEntryTab projectId={projectId} equipment={equipment} materials={materials} onRefresh={() => { fetchFuelIssues(); fetchFuelSummary(); fetchMaterials(); }} />
      )}

      {activeTab === "readings-wizard" && (
        <ReadingsWizardTab projectId={projectId} isMasterAdmin={isMasterAdmin} />
      )}

      {/* Utilization Report Tab */}
      {activeTab === "utilization" && (
        <UtilizationReportTab projectId={projectId} equipment={equipment} />
      )}

      {/* Dialogs */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {dialogType === "equipment" ? (editingEquipment ? 'EDIT EQUIPMENT' : 'ADD EQUIPMENT') :
               dialogType === "daily-log" ? (editingDailyLog ? 'EDIT DAILY LOG' : 'EQUIPMENT DAILY LOG') : 'ISSUE FUEL TO MACHINERY'}
            </DialogTitle>
          </DialogHeader>

          {/* Equipment Form */}
          {dialogType === "equipment" && (
            <form onSubmit={handleEquipmentSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment Name *</label>
                  <input type="text" required value={equipmentForm.name} onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Excavator, Dumper, Roller" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Registration / Asset ID *</label>
                  <input type="text" required value={equipmentForm.equipment_id} onChange={(e) => setEquipmentForm({...equipmentForm, equipment_id: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="DL01AB1234, EXC-001" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Type</label>
                  <select value={equipmentForm.equipment_type} onChange={(e) => setEquipmentForm({...equipmentForm, equipment_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="Heavy">Heavy Equipment</option>
                    <option value="Light">Light Equipment</option>
                    <option value="Vehicle">Vehicle</option>
                    <option value="Generator">Generator</option>
                    <option value="Compressor">Compressor</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Make</label>
                  <input type="text" value={equipmentForm.make} onChange={(e) => setEquipmentForm({...equipmentForm, make: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Tata, JCB, Caterpillar" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Model</label>
                  <input type="text" value={equipmentForm.model} onChange={(e) => setEquipmentForm({...equipmentForm, model: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="3DX, 320D" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Year</label>
                  <input type="number" value={equipmentForm.year_of_manufacture} onChange={(e) => setEquipmentForm({...equipmentForm, year_of_manufacture: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="2020" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Capacity</label>
                  <input type="text" value={equipmentForm.capacity} onChange={(e) => setEquipmentForm({...equipmentForm, capacity: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="20 Ton, 200 HP" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Fuel Type</label>
                  <select value={equipmentForm.fuel_type} onChange={(e) => setEquipmentForm({...equipmentForm, fuel_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="Diesel">Diesel</option>
                    <option value="Petrol">Petrol</option>
                    <option value="Electric">Electric</option>
                    <option value="CNG">CNG</option>
                    <option value="NA">N/A</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Tank (L)</label>
                  <input type="number" step="0.1" value={equipmentForm.tank_capacity} onChange={(e) => setEquipmentForm({...equipmentForm, tank_capacity: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Avg Consumption (L/hr)</label>
                  <input type="number" step="0.1" value={equipmentForm.avg_consumption} onChange={(e) => setEquipmentForm({...equipmentForm, avg_consumption: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Ownership</label>
                  <select value={equipmentForm.owner_type} onChange={(e) => setEquipmentForm({...equipmentForm, owner_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="Own">Own</option>
                    <option value="Hired">Hired</option>
                    <option value="Leased">Leased</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Hire Rate (₹/hr)</label>
                  <input type="number" step="0.01" value={equipmentForm.hire_rate} onChange={(e) => setEquipmentForm({...equipmentForm, hire_rate: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Reading Type</label>
                  <select value={equipmentForm.reading_type} onChange={(e) => setEquipmentForm({...equipmentForm, reading_type: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="equipment-reading-type-select">
                    {READING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Status</label>
                  <select value={equipmentForm.status} onChange={(e) => setEquipmentForm({...equipmentForm, status: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="Available">Available</option>
                    <option value="In Use">In Use</option>
                    <option value="Under Maintenance">Under Maintenance</option>
                    <option value="Breakdown">Breakdown</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary">{editingEquipment ? 'Update' : 'Add'} Equipment</button>
              </div>
            </form>
          )}

          {/* Daily Log Form */}
          {dialogType === "daily-log" && (
            <form onSubmit={handleDailyLogSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment *</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search machinery..."
                      value={equipmentSearch}
                      onChange={(e) => setEquipmentSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-sm rounded-b-none focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                  <select required value={dailyLogForm.equipment_id} onChange={(e) => {
                      setDailyLogForm({...dailyLogForm, equipment_id: e.target.value, km_start_reading: "", km_end_reading: "", hour_start_reading: "", hour_end_reading: ""});
                      if (e.target.value) {
                        fetchLastLogReadings(e.target.value);
                        fetchFuelForLogDate(e.target.value, dailyLogForm.date);
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 border-t-0 rounded-sm rounded-t-none focus:outline-none focus:ring-2 focus:ring-primary"
                    size={Math.min(equipment.filter(eq => !equipmentSearch || eq.name.toLowerCase().includes(equipmentSearch.toLowerCase()) || eq.equipment_id.toLowerCase().includes(equipmentSearch.toLowerCase())).length + 1, 6)}>
                    <option value="">Select Equipment</option>
                    {equipment
                      .filter(eq => !equipmentSearch || eq.name.toLowerCase().includes(equipmentSearch.toLowerCase()) || eq.equipment_id.toLowerCase().includes(equipmentSearch.toLowerCase()))
                      .map((eq) => (
                        <option key={eq.id} value={eq.id}>{eq.name} ({eq.equipment_id})</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                  <input type="date" required value={dailyLogForm.date} onChange={(e) => {
                      setDailyLogForm({...dailyLogForm, date: e.target.value});
                      fetchFuelForLogDate(dailyLogForm.equipment_id, e.target.value);
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
              </div>
              
              {/* Note about readings */}
              {lastLogReadings?.has_previous && (
                <div className="p-3 bg-blue-50 rounded-sm border border-blue-200" data-testid="daily-log-last-readings">
                  <p className="text-xs text-blue-700">
                    <strong>Previous Log ({lastLogReadings.last_date}):</strong> Closing KM: <span className="font-mono">{lastLogReadings.closing_km || 0}</span>, Hour: <span className="font-mono">{lastLogReadings.closing_hour || 0}</span> — Opening readings auto-filled.
                  </p>
                </div>
              )}

              {/* Fuel Issue Warning */}
              {fuelForLogDate && (
                <div className={`p-3 rounded-sm border ${
                  (dailyLogForm.km_end_reading && parseFloat(dailyLogForm.km_end_reading) !== (fuelForLogDate.closing_km_reading || fuelForLogDate.current_km_reading || 0)) ||
                  (dailyLogForm.hour_end_reading && parseFloat(dailyLogForm.hour_end_reading) !== (fuelForLogDate.closing_hour_reading || fuelForLogDate.current_hour_reading || 0))
                    ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-200"
                }`} data-testid="daily-log-fuel-notice">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-700 flex items-center gap-1">
                        <Fuel className="w-3 h-3" /> Fuel Issue on {fuelForLogDate.date} ({fuelForLogDate.issue_number})
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Fuel readings: KM <span className="font-mono font-medium">{fuelForLogDate.closing_km_reading || fuelForLogDate.current_km_reading || 0}</span>, 
                        Hour <span className="font-mono font-medium">{fuelForLogDate.closing_hour_reading || fuelForLogDate.current_hour_reading || 0}</span>
                      </p>
                      {((dailyLogForm.km_end_reading && parseFloat(dailyLogForm.km_end_reading) !== (fuelForLogDate.closing_km_reading || fuelForLogDate.current_km_reading || 0)) ||
                        (dailyLogForm.hour_end_reading && parseFloat(dailyLogForm.hour_end_reading) !== (fuelForLogDate.closing_hour_reading || fuelForLogDate.current_hour_reading || 0))) && (
                        <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Your closing readings differ from fuel issue
                        </p>
                      )}
                    </div>
                    <button type="button" onClick={copyFuelReadingsToLog} className="text-xs text-primary hover:underline whitespace-nowrap" data-testid="copy-fuel-readings-btn">
                      Copy to form →
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Machine Status</label>
                <select value={dailyLogForm.machine_status} onChange={(e) => setDailyLogForm({...dailyLogForm, machine_status: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" data-testid="daily-log-status-select">
                  {MACHINE_STATUSES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>

              {/* KM and Hour Meter Readings */}
              <div className={`grid gap-4 ${logEqKm && logEqHr ? "grid-cols-2" : "grid-cols-1"}`}>
                {logEqKm && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="daily-log-km-block">
                  <label className="block text-[10px] font-medium text-slate-600 mb-2 uppercase tracking-widest">Odometer (KM) Readings</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-1">Opening</label>
                      <input type="number" step="0.1" value={dailyLogForm.km_start_reading}
                        onChange={(e) => setDailyLogForm({...dailyLogForm, km_start_reading: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                        data-testid="daily-log-km-start" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-1">Closing</label>
                      <input type="number" step="0.1" value={dailyLogForm.km_end_reading}
                        onChange={(e) => setDailyLogForm({...dailyLogForm, km_end_reading: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                        data-testid="daily-log-km-end" placeholder="0" />
                    </div>
                  </div>
                  {dailyLogForm.km_end_reading && dailyLogForm.km_start_reading && parseFloat(dailyLogForm.km_end_reading) > parseFloat(dailyLogForm.km_start_reading) && (
                    <p className="text-xs text-green-700 mt-1 font-mono">KM Run: {(parseFloat(dailyLogForm.km_end_reading) - parseFloat(dailyLogForm.km_start_reading)).toFixed(1)}</p>
                  )}
                  {dailyLogForm.km_end_reading && dailyLogForm.km_start_reading && parseFloat(dailyLogForm.km_end_reading) < parseFloat(dailyLogForm.km_start_reading) && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="daily-log-km-warning"><AlertTriangle className="w-3 h-3" /> Closing KM is less than Opening</p>
                  )}
                </div>
                )}
                {logEqHr && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="daily-log-hour-block">
                  <label className="block text-[10px] font-medium text-slate-600 mb-2 uppercase tracking-widest">Hour Meter Readings</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-1">Opening</label>
                      <input type="number" step="0.1" value={dailyLogForm.hour_start_reading}
                        onChange={(e) => setDailyLogForm({...dailyLogForm, hour_start_reading: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                        data-testid="daily-log-hour-start" placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-[9px] text-slate-500 mb-1">Closing</label>
                      <input type="number" step="0.1" value={dailyLogForm.hour_end_reading}
                        onChange={(e) => setDailyLogForm({...dailyLogForm, hour_end_reading: e.target.value})}
                        className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
                        data-testid="daily-log-hour-end" placeholder="0" />
                    </div>
                  </div>
                  {dailyLogForm.hour_end_reading && dailyLogForm.hour_start_reading && parseFloat(dailyLogForm.hour_end_reading) > parseFloat(dailyLogForm.hour_start_reading) && (
                    <p className="text-xs text-green-700 mt-1 font-mono">Hours: {(parseFloat(dailyLogForm.hour_end_reading) - parseFloat(dailyLogForm.hour_start_reading)).toFixed(1)}</p>
                  )}
                  {dailyLogForm.hour_end_reading && dailyLogForm.hour_start_reading && parseFloat(dailyLogForm.hour_end_reading) < parseFloat(dailyLogForm.hour_start_reading) && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="daily-log-hour-warning"><AlertTriangle className="w-3 h-3" /> Closing Hour is less than Opening</p>
                  )}
                </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">KM Run (auto-calculated)</label>
                  <input type="number" step="0.1" readOnly
                    value={dailyLogForm.km_end_reading && dailyLogForm.km_start_reading && parseFloat(dailyLogForm.km_end_reading) >= parseFloat(dailyLogForm.km_start_reading)
                      ? (parseFloat(dailyLogForm.km_end_reading) - parseFloat(dailyLogForm.km_start_reading)).toFixed(1)
                      : ""}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm bg-slate-50 font-mono cursor-not-allowed"
                    placeholder="From KM readings"
                    data-testid="daily-log-km-run" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Hours Used (auto or manual){dailyLogForm.machine_status === "Working" ? " *" : ""}</label>
                  <input type="number" step="0.1" required={dailyLogForm.machine_status === "Working"} 
                    value={dailyLogForm.hour_end_reading && dailyLogForm.hour_start_reading && parseFloat(dailyLogForm.hour_end_reading) > parseFloat(dailyLogForm.hour_start_reading)
                      ? (parseFloat(dailyLogForm.hour_end_reading) - parseFloat(dailyLogForm.hour_start_reading)).toFixed(1)
                      : dailyLogForm.hours_used}
                    onChange={(e) => setDailyLogForm({...dailyLogForm, hours_used: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="From Hour readings or enter manually"
                    data-testid="daily-log-hours-used" />
                  <p className="text-[10px] text-slate-500 mt-1">Auto-fills from Hour meter. Enter manually if no Hour readings.</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Operator Name *</label>
                  <input type="text" required value={dailyLogForm.operator_name} onChange={(e) => setDailyLogForm({...dailyLogForm, operator_name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Activity *</label>
                <input type="text" required value={dailyLogForm.activity} onChange={(e) => setDailyLogForm({...dailyLogForm, activity: e.target.value})}
                  data-testid="daily-log-activity-input"
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Earthwork, Loading, Transportation" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Location</label>
                  <input type="text" value={dailyLogForm.location} onChange={(e) => setDailyLogForm({...dailyLogForm, location: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ch 445+000" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Breakdown Hours</label>
                  <input type="number" step="0.1" value={dailyLogForm.breakdown_hours} onChange={(e) => setDailyLogForm({...dailyLogForm, breakdown_hours: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Breakdown Reason</label>
                  <input type="text" value={dailyLogForm.breakdown_reason} onChange={(e) => setDailyLogForm({...dailyLogForm, breakdown_reason: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Performance Metric</label>
                  <input type="number" step="0.1" value={dailyLogForm.performance_metric} onChange={(e) => setDailyLogForm({...dailyLogForm, performance_metric: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    placeholder="e.g., Distance covered, Fuel consumed" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Performance Unit</label>
                  <select value={dailyLogForm.performance_unit} onChange={(e) => setDailyLogForm({...dailyLogForm, performance_unit: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="KM/hr">KM/hr (Distance per hour)</option>
                    <option value="KM/lit">KM/lit (Fuel efficiency)</option>
                    <option value="Litre/hr">Litre/hr (Fuel per hour)</option>
                    <option value="Trips/day">Trips/day</option>
                    <option value="Cum/hr">Cum/hr (Volume per hour)</option>
                    <option value="MT/hr">MT/hr (Tons per hour)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                <textarea value={dailyLogForm.remarks} onChange={(e) => setDailyLogForm({...dailyLogForm, remarks: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" rows="2" />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary bg-accent hover:bg-accent/90">
                  {editingDailyLog ? 'Update' : 'Record'} Daily Log
                </button>
              </div>
            </form>
          )}

          {/* Fuel Issue Form */}
          {dialogType === "fuel" && (
            <form onSubmit={handleFuelSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Equipment / Machinery *</label>
                  <select required value={fuelForm.equipment_id} onChange={(e) => {
                    setFuelForm({...fuelForm, equipment_id: e.target.value, daily_log_id: ""});
                    fetchLastFuelReadings(e.target.value);
                    fetchLogsForLinking(e.target.value, fuelForm.date);
                  }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select Equipment</option>
                    {equipment.filter(eq => eq.fuel_type !== "Electric" && eq.fuel_type !== "NA").map((eq) => (
                      <option key={eq.id} value={eq.id}>{eq.name} ({eq.equipment_id}) - {eq.fuel_type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Fuel Material *</label>
                  <select required value={fuelForm.material_id} onChange={(e) => setFuelForm({...fuelForm, material_id: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary">
                    <option value="">Select Fuel</option>
                    {materials.map((m) => (
                      <option key={m.id} value={m.id}>{m.name} (Stock: {m.current_stock?.toFixed(0)} {m.unit})</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Show last fuel issue info if equipment selected */}
              {lastFuelReadings && lastFuelReadings.has_previous && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
                  <p className="text-blue-800 font-medium">Last Fuel Issue: {lastFuelReadings.last_date}</p>
                  <p className="text-blue-600">Closing KM: {lastFuelReadings.closing_km_reading} | Closing Hr: {lastFuelReadings.closing_hour_reading} | Qty: {lastFuelReadings.last_quantity}L</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Date *</label>
                  <input type="date" required value={fuelForm.date} onChange={(e) => {
                    setFuelForm({...fuelForm, date: e.target.value, daily_log_id: ""});
                    if (fuelForm.equipment_id) fetchLogsForLinking(fuelForm.equipment_id, e.target.value);
                  }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Quantity (Liters) *</label>
                  <input type="number" step="0.1" required value={fuelForm.quantity} onChange={(e) => setFuelForm({...fuelForm, quantity: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono" />
                </div>
              </div>
              
              {/* Opening Readings (auto-populated, read-only) */}
              <div className={`grid gap-4 ${fuelEqKm && fuelEqHr ? "grid-cols-2" : "grid-cols-1"}`}>
                {fuelEqHr && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Opening Hour Reading</label>
                  <input type="number" value={fuelForm.opening_hour_reading} readOnly
                    className="w-full px-4 py-2 border border-slate-200 rounded-sm bg-slate-100 font-mono text-slate-600"
                    placeholder="Auto from last issue" />
                </div>
                )}
                {fuelEqKm && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Opening KM Reading</label>
                  <input type="number" value={fuelForm.opening_km_reading} readOnly
                    className="w-full px-4 py-2 border border-slate-200 rounded-sm bg-slate-100 font-mono text-slate-600"
                    placeholder="Auto from last issue" />
                </div>
                )}
              </div>
              
              {/* Closing Readings (user enters) */}
              <div className={`grid gap-4 ${fuelEqKm && fuelEqHr ? "grid-cols-2" : "grid-cols-1"}`}>
                {fuelEqHr && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Closing Hour Reading *</label>
                  <input type="number" step="0.1" value={fuelForm.current_hour_reading} onChange={(e) => setFuelForm({...fuelForm, current_hour_reading: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    data-testid="fuel-closing-hour" placeholder="Current hour meter reading" />
                  {fuelForm.current_hour_reading && parseFloat(fuelForm.current_hour_reading) < fuelForm.opening_hour_reading && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="fuel-hour-warning"><AlertTriangle className="w-3 h-3" /> Closing Hour less than Opening ({fuelForm.opening_hour_reading})</p>
                  )}
                </div>
                )}
                {fuelEqKm && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Closing KM Reading *</label>
                  <input type="number" step="0.1" value={fuelForm.current_km_reading} onChange={(e) => setFuelForm({...fuelForm, current_km_reading: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                    data-testid="fuel-closing-km" placeholder="Current odometer reading" />
                  {fuelForm.current_km_reading && parseFloat(fuelForm.current_km_reading) < fuelForm.opening_km_reading && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1" data-testid="fuel-km-warning"><AlertTriangle className="w-3 h-3" /> Closing KM less than Opening ({fuelForm.opening_km_reading})</p>
                  )}
                </div>
                )}
              </div>
              
              {/* Link to Daily Log (optional) */}
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3" data-testid="fuel-link-log-section">
                <label className="block text-xs font-medium text-blue-800 mb-2 uppercase tracking-widest">Link to Daily Log (Optional)</label>
                <select value={fuelForm.daily_log_id} onChange={(e) => setFuelForm({...fuelForm, daily_log_id: e.target.value})}
                  className="w-full px-3 py-2 border border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  data-testid="fuel-link-log-select">
                  <option value="">-- No link (fuel issue not tied to a specific log) --</option>
                  {logsForLinking.map(log => (
                    <option key={log.id} value={log.id}>{log.date} | {log.hours_used}hrs | {log.activity} | {log.operator_name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-blue-600 mt-1">Shows logs from ±3 days of selected date for this machine</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Issued By *</label>
                  <input type="text" required value={fuelForm.issued_by} onChange={(e) => setFuelForm({...fuelForm, issued_by: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Received By (Operator) *</label>
                  <input type="text" required value={fuelForm.received_by} onChange={(e) => setFuelForm({...fuelForm, received_by: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Remarks</label>
                <textarea value={fuelForm.remarks} onChange={(e) => setFuelForm({...fuelForm, remarks: e.target.value})}
                  className="w-full px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary" rows="2" />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeDialog} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary bg-warning hover:bg-warning/90 text-white" data-testid="fuel-submit-btn">
                  {editingFuelIssue ? 'Update Fuel Issue' : 'Issue Fuel'}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Log History Dialog */}
      <Dialog open={logHistoryDialogOpen} onOpenChange={setLogHistoryDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="log-history-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-heading text-2xl">
                DAILY LOG HISTORY
                {logHistoryEquipment && (
                  <span className="text-base font-normal text-slate-600 ml-2">
                    - {logHistoryEquipment.name} ({logHistoryEquipment.equipment_id})
                  </span>
                )}
              </DialogTitle>
              <button onClick={exportLogHistory} className="btn-outline flex items-center gap-2 text-sm" data-testid="log-history-export-btn">
                <Download className="w-4 h-4" /> Export Excel
              </button>
            </div>
          </DialogHeader>

          {/* Tab Switcher */}
          <div className="flex gap-2 mt-2 border-b border-slate-200">
            <button onClick={() => setLogHistoryTab("table")} 
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${logHistoryTab === "table" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="log-history-tab-table">
              Table View
            </button>
            <button onClick={() => setLogHistoryTab("chart")} 
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${logHistoryTab === "chart" ? "border-primary text-primary" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid="log-history-tab-chart">
              Reading Trends
            </button>
          </div>

          {/* Chart View */}
          {logHistoryTab === "chart" && logHistory.length > 0 && (
            <div className="mt-4 p-4 bg-slate-50 rounded-sm" data-testid="log-history-chart">
              {(() => {
                const sorted = [...logHistory].sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
                const kmValues = sorted.map(l => l.km_end_reading || 0);
                const hrValues = sorted.map(l => l.hour_end_reading || 0);
                const kmMin = Math.min(...kmValues), kmMax = Math.max(...kmValues);
                const hrMin = Math.min(...hrValues), hrMax = Math.max(...hrValues);
                const kmRange = kmMax - kmMin || 1;
                const hrRange = hrMax - hrMin || 1;
                const w = 800, h = 200, pad = 40;

                return (
                  <div>
                    <p className="text-xs text-slate-500 mb-3 uppercase tracking-widest">Last {sorted.length} entries (KM in blue, Hours in green)</p>
                    <svg viewBox={`0 0 ${w} ${h + 40}`} className="w-full h-auto">
                      {/* Grid lines */}
                      {[0, 0.25, 0.5, 0.75, 1].map(p => (
                        <line key={p} x1={pad} y1={pad + (1 - p) * h} x2={w - pad} y2={pad + (1 - p) * h} stroke="#e2e8f0" strokeWidth="1" />
                      ))}
                      {/* KM Line */}
                      <polyline
                        fill="none" stroke="#3b82f6" strokeWidth="2"
                        points={sorted.map((l, i) => `${pad + i * ((w - 2 * pad) / (sorted.length - 1 || 1))},${pad + (1 - (l.km_end_reading - kmMin) / kmRange) * h}`).join(" ")}
                      />
                      {/* Hour Line */}
                      <polyline
                        fill="none" stroke="#22c55e" strokeWidth="2"
                        points={sorted.map((l, i) => `${pad + i * ((w - 2 * pad) / (sorted.length - 1 || 1))},${pad + (1 - (l.hour_end_reading - hrMin) / hrRange) * h}`).join(" ")}
                      />
                      {/* Points */}
                      {sorted.map((l, i) => {
                        const x = pad + i * ((w - 2 * pad) / (sorted.length - 1 || 1));
                        const yKm = pad + (1 - (l.km_end_reading - kmMin) / kmRange) * h;
                        const yHr = pad + (1 - (l.hour_end_reading - hrMin) / hrRange) * h;
                        return (
                          <g key={i}>
                            <circle cx={x} cy={yKm} r="4" fill="#3b82f6" />
                            <circle cx={x} cy={yHr} r="4" fill="#22c55e" />
                            {i % Math.ceil(sorted.length / 8) === 0 && (
                              <text x={x} y={h + pad + 15} textAnchor="middle" className="text-[9px] fill-slate-500">{l.date.slice(5)}</text>
                            )}
                          </g>
                        );
                      })}
                      {/* Y-axis labels */}
                      <text x={pad - 5} y={pad} textAnchor="end" className="text-[9px] fill-blue-600">{kmMax.toFixed(0)}</text>
                      <text x={pad - 5} y={pad + h} textAnchor="end" className="text-[9px] fill-blue-600">{kmMin.toFixed(0)}</text>
                      <text x={w - pad + 5} y={pad} textAnchor="start" className="text-[9px] fill-green-600">{hrMax.toFixed(0)}</text>
                      <text x={w - pad + 5} y={pad + h} textAnchor="start" className="text-[9px] fill-green-600">{hrMin.toFixed(0)}</text>
                    </svg>
                    <div className="flex justify-center gap-6 mt-2 text-xs">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded-full"></span> KM Reading ({kmMin.toFixed(0)} - {kmMax.toFixed(0)})</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-full"></span> Hour Reading ({hrMin.toFixed(0)} - {hrMax.toFixed(0)})</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {logHistoryTab === "chart" && logHistory.length === 0 && (
            <div className="text-center py-8 text-slate-500">No data to display chart.</div>
          )}

          {/* Table View */}
          {logHistoryTab === "table" && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm" data-testid="log-history-table">
              <thead>
                <tr className="border-b-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-600">
                  <th className="py-2 px-2 text-left">Date</th>
                  <th className="py-2 px-2 text-right">KM Open</th>
                  <th className="py-2 px-2 text-right">KM Close</th>
                  <th className="py-2 px-2 text-right">KM Run</th>
                  <th className="py-2 px-2 text-right">Hr Open</th>
                  <th className="py-2 px-2 text-right">Hr Close</th>
                  <th className="py-2 px-2 text-right">Hours</th>
                  <th className="py-2 px-2 text-left">Operator</th>
                  <th className="py-2 px-2 text-left">Activity</th>
                  <th className="py-2 px-2 text-left">Fuel Linked</th>
                  {isMasterAdmin && <th className="py-2 px-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {logHistory.map(log => (
                  <tr key={log.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`log-history-row-${log.id}`}>
                    <td className="py-2 px-2 font-mono">{log.date}</td>
                    <td className="py-2 px-2 font-mono text-right">{log.km_start_reading || 0}</td>
                    <td className="py-2 px-2 font-mono text-right">{log.km_end_reading || 0}</td>
                    <td className="py-2 px-2 font-mono text-right font-medium text-blue-600">{log.km_used?.toFixed(1) || Math.max(0, (log.km_end_reading || 0) - (log.km_start_reading || 0)).toFixed(1)}</td>
                    <td className="py-2 px-2 font-mono text-right">{log.hour_start_reading || 0}</td>
                    <td className="py-2 px-2 font-mono text-right">{log.hour_end_reading || 0}</td>
                    <td className="py-2 px-2 font-mono text-right font-medium text-green-600">{log.hours_used?.toFixed(1)}</td>
                    <td className="py-2 px-2">{log.operator_name}</td>
                    <td className="py-2 px-2 max-w-[150px] truncate" title={log.activity}>{log.activity}</td>
                    <td className="py-2 px-2">
                      {log.linked_fuel?.length > 0 ? (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {log.linked_fuel.reduce((s, f) => s + f.quantity, 0)}L ({log.linked_fuel.length})
                        </span>
                      ) : <span className="text-slate-400">-</span>}
                    </td>
                    {isMasterAdmin && (
                      <td className="py-2 px-2">
                        <button onClick={() => { setLogHistoryDialogOpen(false); handleEditDailyLog(log); }}
                          className="text-primary hover:text-primary/80" data-testid={`edit-log-${log.id}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {logHistory.length === 0 && (
              <div className="text-center py-8 text-slate-500">No daily logs found for this equipment.</div>
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Equipment Documents Dialog */}
      <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              EQUIPMENT DOCUMENTS
              {selectedEquipmentForDocs && (
                <span className="text-base font-normal text-slate-600 ml-2">
                  - {selectedEquipmentForDocs.name} ({selectedEquipmentForDocs.equipment_id})
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Upload Form */}
          <form onSubmit={handleDocumentUpload} className="space-y-4 mt-4 p-4 bg-slate-50 rounded-sm">
            <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-700">Upload New Document</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Document Type *</label>
                <select
                  required
                  value={docForm.document_type}
                  onChange={(e) => setDocForm({...docForm, document_type: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                >
                  <option value="">Select Type</option>
                  {equipmentDocTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Document Name</label>
                <input
                  type="text"
                  value={docForm.document_name}
                  onChange={(e) => setDocForm({...docForm, document_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  placeholder="Optional custom name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">File (PDF/JPG/PNG) *</label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setDocForm({...docForm, file: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={uploadingDoc}
                className="btn-primary flex items-center space-x-2 text-sm"
              >
                <Upload className="w-4 h-4" />
                <span>{uploadingDoc ? "Uploading..." : "Upload Document"}</span>
              </button>
            </div>
          </form>

          {/* Documents List */}
          <div className="mt-6">
            <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-700 mb-4">
              Uploaded Documents ({equipmentDocuments.length})
            </h3>
            {equipmentDocuments.length > 0 ? (
              <div className="space-y-2">
                {equipmentDocuments.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-sm hover:bg-slate-50">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary/10 rounded-sm">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{doc.document_name}</p>
                        <p className="text-xs text-slate-500">
                          {doc.document_type} • {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleDocumentDownload(doc)}
                        className="p-2 hover:bg-slate-200 rounded-sm"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-primary" />
                      </button>
                      <button
                        onClick={() => handleDocumentDelete(doc)}
                        className="p-2 hover:bg-red-100 rounded-sm"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-error" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <File className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No documents uploaded yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Equipment Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Archive className="w-5 h-5 text-amber-600" />
              Archive Equipment (De-hire)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm">
              <p className="text-sm text-amber-800">
                You are about to archive <span className="font-semibold">{equipmentToArchive?.name}</span> ({equipmentToArchive?.equipment_id}).
              </p>
              <p className="text-xs text-amber-600 mt-1">
                The equipment will be moved to archives and can be restored later if needed.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Reason for De-hiring <span className="text-error">*</span>
              </label>
              <select
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="w-full p-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select a reason</option>
                <option value="Contract Ended">Contract Ended</option>
                <option value="Equipment Returned">Equipment Returned to Owner</option>
                <option value="Not Required">No Longer Required</option>
                <option value="Replaced">Replaced with New Equipment</option>
                <option value="Sold">Equipment Sold</option>
                <option value="Breakdown - Unrepairable">Breakdown - Unrepairable</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Additional Remarks
              </label>
              <textarea
                value={archiveRemarks}
                onChange={(e) => setArchiveRemarks(e.target.value)}
                placeholder="Any additional details..."
                className="w-full p-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary h-20 resize-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setArchiveDialogOpen(false)}
              className="px-4 py-2 border border-slate-300 rounded-sm text-slate-700 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              onClick={handleArchiveEquipment}
              disabled={!archiveReason}
              className="px-4 py-2 bg-amber-600 text-white rounded-sm hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Archive Equipment
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EquipmentManagement;
